/**
 * File Upload Component
 * Multi-file upload interface for a batch
 */
import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Chip,
  Grid,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error,
  Delete,
  InsertDriveFile,
  ArrowForward,
} from '@mui/icons-material';
import uploadApiService from '@/services/uploadApi';
import { useTranslation } from 'react-i18next';

interface FileWithStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  message?: string;
}

const FileUpload: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // State
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // File validation
  const ALLOWED_TYPES = ['application/pdf', 'text/html', 'message/rfc822'];
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
  
  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type) && !file.name.toLowerCase().match(/\.(pdf|html|htm|eml)$/)) {
      return t('upload.validation.invalid_type', 'Invalid file type. Only PDF, HTML, and EML files are allowed.');
    }
    if (file.size > MAX_FILE_SIZE) {
      return t('upload.validation.file_too_large', 'File is too large. Maximum size is 50 MB.');
    }
    return null;
  };
  
  // Handle file selection
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    
    const newFiles: FileWithStatus[] = selectedFiles.map((file) => {
      const validationError = validateFile(file);
      return {
        file,
        status: validationError ? 'error' : 'pending',
        message: validationError || undefined,
      };
    });
    
    setFiles((prev) => [...prev, ...newFiles]);
    setError(null);
  }, []);
  
  // Handle drag and drop
  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    
    const newFiles: FileWithStatus[] = droppedFiles.map((file) => {
      const validationError = validateFile(file);
      return {
        file,
        status: validationError ? 'error' : 'pending',
        message: validationError || undefined,
      };
    });
    
    setFiles((prev) => [...prev, ...newFiles]);
    setError(null);
  }, []);
  
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };
  
  // Remove file
  const handleRemove = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };
  
  // Upload files
  const handleUpload = async () => {
    if (!batchId) {
      setError(t('upload.errors.no_batch_id', 'No batch ID provided'));
      return;
    }
    
    const validFiles = files.filter((f) => f.status === 'pending');
    if (validFiles.length === 0) {
      setError(t('upload.errors.no_valid_files', 'No valid files to upload'));
      return;
    }
    
    setUploading(true);
    setError(null);
    
    try {
      // Mark files as uploading
      setFiles((prev) =>
        prev.map((f) => (f.status === 'pending' ? { ...f, status: 'uploading' } : f))
      );
      
      const response = await uploadApiService.uploadFiles(
        batchId,
        validFiles.map((f) => f.file)
      );
      
      console.log('✅ Files uploaded:', response);
      
      // Update file statuses based on response
      setFiles((prev) =>
        prev.map((fileWithStatus) => {
          const uploadedFile = response.files.find(
            (f) => f.filename === fileWithStatus.file.name
          );
          
          if (uploadedFile) {
            return {
              ...fileWithStatus,
              status: uploadedFile.status === 'uploaded' ? 'success' : 'error',
              message: uploadedFile.message,
            };
          }
          return fileWithStatus;
        })
      );
      
      setUploadComplete(true);
      
      // Redirect immediately to batch details page
      navigate(`/upload/batch/${batchId}`);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.message || t('upload.errors.upload_failed', 'Failed to upload files');
      setError(errorMessage);
      console.error('❌ Upload failed:', err);
      
      // Mark all uploading files as error
      setFiles((prev) =>
        prev.map((f) => (f.status === 'uploading' ? { ...f, status: 'error', message: errorMessage } : f))
      );
    } finally {
      setUploading(false);
    }
  };
  
  // Navigate to batch list
  const handleViewBatches = () => {
    navigate('/upload/batches');
  };
  
  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const successCount = files.filter((f) => f.status === 'success').length;
  const errorCount = files.filter((f) => f.status === 'error').length;
  
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            {t('upload.files.title', 'Upload Files')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('upload.files.batch_id', 'Batch ID')}: <Chip label={batchId} size="small" />
          </Typography>
        </Box>
        
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {/* Success Alert */}
        {uploadComplete && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('upload.files.success', `Successfully uploaded ${successCount} file(s). ${errorCount > 0 ? `${errorCount} failed.` : ''}`)}
          </Alert>
        )}
        
        {/* Drop Zone */}
        {!uploadComplete && (
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{
              border: '2px dashed',
              borderColor: 'primary.main',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              mb: 3,
              backgroundColor: 'action.hover',
              cursor: 'pointer',
            }}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('upload.files.drop_zone', 'Drag & drop files here')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('upload.files.or', 'or')}
            </Typography>
            <Button variant="outlined" component="span">
              {t('upload.files.browse', 'Browse Files')}
            </Button>
            <input
              id="file-input"
              type="file"
              multiple
              accept=".pdf,.html,.htm,.eml"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
              {t('upload.files.supported', 'Supported: PDF, HTML, EML • Max 50 MB per file')}
            </Typography>
          </Box>
        )}
        
        {/* File List */}
        {files.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('upload.files.selected', `Selected Files (${files.length})`)}
            </Typography>
            
            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip label={`${pendingCount} pending`} size="small" color="default" />
              <Chip label={`${successCount} uploaded`} size="small" color="success" />
              {errorCount > 0 && <Chip label={`${errorCount} failed`} size="small" color="error" />}
            </Box>
            
            <List>
              {files.map((fileWithStatus, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    !uploading && fileWithStatus.status === 'pending' && (
                      <IconButton edge="end" onClick={() => handleRemove(index)}>
                        <Delete />
                      </IconButton>
                    )
                  }
                >
                  <ListItemIcon>
                    {fileWithStatus.status === 'success' ? (
                      <CheckCircle color="success" />
                    ) : fileWithStatus.status === 'error' ? (
                      <Error color="error" />
                    ) : fileWithStatus.status === 'uploading' ? (
                      <CircularProgress size={24} />
                    ) : (
                      <InsertDriveFile />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={fileWithStatus.file.name}
                    secondary={
                      fileWithStatus.message ||
                      `${(fileWithStatus.file.size / 1024 / 1024).toFixed(2)} MB`
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        
        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress />
            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
              {t('upload.files.uploading', 'Uploading files...')}
            </Typography>
          </Box>
        )}
        
        {/* Actions */}
        <Grid container spacing={2}>
          {!uploadComplete ? (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={pendingCount === 0 || uploading}
                onClick={handleUpload}
                startIcon={<CloudUpload />}
              >
                {uploading
                  ? t('upload.files.uploading_btn', 'Uploading...')
                  : t('upload.files.upload_btn', `Upload ${pendingCount} File(s)`)
                }
              </Button>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleViewBatches}
                endIcon={<ArrowForward />}
              >
                {t('upload.files.view_batches', 'View All Batches')}
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default FileUpload;

